import * as THREE from 'three';
import { MeshStandardNodeMaterial } from 'three/webgpu';
import {
  cameraPosition,
  normalWorld,
  positionWorld,
  uniform,
  vertexColor,
  wgslFn,
} from 'three/tsl';
import type { Track } from '../track/TrackLoader';
import { trackSurfaceHeightAtArc } from '../track/TrackElevation';
import {
  canyonOpenness,
  canyonWallDistance,
  canyonWallHeight,
  isCanyonLandscape,
  type LandscapeOptions,
} from './Landscape';
import type { SurfaceTextureSet } from './SurfaceTextures';
import { DEFAULT_STYLE_CONFIG, type StyleConfig } from './StyleConfig';
import type { BakedPbrSurface } from './BakedSurfaceAssets';
import { installAerialPerspective } from './AerialPerspective';
import { installValueComposition, tagValueBucket } from './ValueComposition';
import type { RendererMode } from './Renderer';

/**
 * Continuous desert-canyon terrain.
 *
 * One large vertex-displaced plane covering the full visible area. Multi-octave
 * fbm + ridge noise drives height; a distance-to-track envelope keeps the
 * racing corridor flat (height = TERRAIN_BASE_Y) while letting the surrounding
 * terrain rise into rolling dunes and rocky buttes the further out you sample.
 *
 * `sampleHeight(x, z)` is exported so prop placers (rocks, gantries, pylons)
 * can sit flush on the actual terrain instead of clipping through hillsides.
 */

export const TERRAIN_BASE_Y = -0.15;
const FLAT_RADIUS = 30;        // metres of guaranteed flat zone around any centerline point
const RAMP_LENGTH = 90;        // metres over which envelope rises 0 → 1
const TERRAIN_PADDING = 250;   // metres of mesh extent past the track bounds
const TERRAIN_SEGMENTS = 192;  // grid resolution; 192² ≈ 75k triangles
const TERRAIN_SEGMENTS_CANYON = 192;
const TERRAIN_SEGMENTS_CANYON_PREVIEW = 144;
const makeNodeUniform = uniform as any;
const makeNodeVertexColor = vertexColor as any;
const makeWgslFn = wgslFn as any;
// Terrain is split into a CHUNK_COUNT² grid of sub-meshes that share the same
// material. Each chunk gets its own tight bounding sphere, so Three.js's
// built-in frustum culling drops the chunks outside the camera view —
// reclaiming the ~50% of terrain fragment cost that's behind/beside the
// racer in chase cam. Trade-off: 16 draw calls instead of 1, which is
// trivial on M1 Max and a worthwhile spend vs the ~7ms GPU we measured
// when the full mesh was visible.
const TERRAIN_CHUNK_COUNT = 4;

// Sunset desert palette. Bands run shadow → warm rust valley → mid sand →
// sunlit ridge → bright peak rim. Colors are now driven by style.ground in
// the StyleConfig so they can be tuned live and saved into the Hero preset.
// Shadow blends in based on slope + how much the face turns away from the
// sun, baking a painted-canyon directional light into the vertex colours so
// we keep one cheap material.
const SUN_DIR = new THREE.Vector3(72, 98, 48).normalize();

interface TerrainPalette {
  shadow: THREE.Color;
  valley: THREE.Color;
  sand: THREE.Color;
  ridge: THREE.Color;
  peak: THREE.Color;
}

function paletteFromStyle(ground: StyleConfig['ground']): TerrainPalette {
  return {
    shadow: new THREE.Color(ground.shadowHex),
    valley: new THREE.Color(ground.valleyHex),
    sand: new THREE.Color(ground.sandHex),
    ridge: new THREE.Color(ground.ridgeHex),
    peak: new THREE.Color(ground.peakHex),
  };
}

export interface TerrainResult {
  /**
   * The terrain root. Currently a `Group` named `desert-terrain` whose
   * children are the per-chunk meshes. Callers should treat this as an
   * opaque `Object3D` — adding it to the scene, toggling `.visible`, and
   * `traverse()` all behave the same as with a single mesh.
   */
  mesh: THREE.Object3D;
  sampleHeight: (x: number, z: number) => number;
  /** Apply a new ground style (and re-paint vertices if palette changed). */
  applyStyle: (style: StyleConfig) => void;
}

export interface TerrainCorridorBlend {
  (x: number, z: number): { mask: number; roadBaseY: number };
}

export type TerrainShaderMode = 'fast' | 'detailed' | 'bakedPbr';

export interface BuildTerrainOptions extends LandscapeOptions {
  sandTextures?: SurfaceTextureSet;
  quality?: 'preview' | 'full';
  /** Detailed is the authored path; fast is an explicit perf diagnostic A/B mode. */
  shaderMode?: TerrainShaderMode;
  bakedSurface?: BakedPbrSurface | null;
  /** Style block driving the palette + procedural shader. */
  style?: StyleConfig;
  rendererMode?: RendererMode;
  /** Optional authored corridor mask; classic sinusoidal flattening remains the fallback. */
  corridorBlend?: TerrainCorridorBlend;
}

export function buildTerrain(track: Track, options: BuildTerrainOptions = {}): TerrainResult {
  const style = options.style ?? DEFAULT_STYLE_CONFIG;
  const palette = paletteFromStyle(style.ground);

  const bounds = trackBounds(track);
  const cx = (bounds.minX + bounds.maxX) / 2;
  const cz = (bounds.minZ + bounds.maxZ) / 2;
  const sizeX = (bounds.maxX - bounds.minX) + TERRAIN_PADDING * 2;
  const sizeZ = (bounds.maxZ - bounds.minZ) + TERRAIN_PADDING * 2;
  const size = Math.max(sizeX, sizeZ);

  const grid = buildCenterlineGrid(track, 110);
  const canyon = isCanyonLandscape(options.version);

  const sample = (x: number, z: number): number => {
    const nearest = nearestCenterlineSample(grid, track, x, z);
    const height = canyon
      ? sampleCanyonTerrain(track, x, z, nearest)
      : sampleClassicTerrain(track, x, z, nearest);
    const corridor = options.corridorBlend?.(x, z);
    return corridor ? lerp(height, corridor.roadBaseY, corridor.mask) : height;
  };

  const segments = canyon
    ? options.quality === 'preview' ? TERRAIN_SEGMENTS_CANYON_PREVIEW : TERRAIN_SEGMENTS_CANYON
    : TERRAIN_SEGMENTS;
  const geom = new THREE.PlaneGeometry(size, size, segments, segments);
  geom.rotateX(-Math.PI / 2);
  geom.translate(cx, 0, cz);

  const positions = geom.getAttribute('position') as THREE.BufferAttribute;

  // Pass 1 — break the regular grid off the racing surface, then displace
  // each vertex along its column. Steep canyon faces otherwise reveal the
  // plane tessellation as repeating vertical wall facets in chase view.
  for (let i = 0; i < positions.count; i++) {
    let x = positions.getX(i);
    let z = positions.getZ(i);
    if (canyon) {
      const nearest = nearestCenterlineSample(grid, track, x, z);
      const offTrack = smoothstep(70, 185, Math.abs(nearest.signedLateral));
      if (offTrack > 0) {
        const jitter = (0.35 + offTrack * 1.15);
        const jx = (fbm2(x * 0.025 + 17.3, z * 0.025, 2) - 0.5) * jitter;
        const jz = (fbm2(x * 0.025, z * 0.025 + 29.1, 2) - 0.5) * jitter;
        const absLat = Math.abs(nearest.signedLateral);
        const side = nearest.signedLateral >= 0 ? 1 : -1;
        const faceWarp = smoothstep(92, 235, absLat);
        const lateralWarp = (ridgeFbm(nearest.arcLength * 0.007 + side * 4.1, absLat * 0.021, 3) - 0.42)
          * faceWarp
          * (1.8 + faceWarp * 4.2);
        const alongWarp = (fbm2(nearest.arcLength * 0.010 + 17.3, absLat * 0.017 + side * 2.6, 2) - 0.5)
          * faceWarp
          * (0.7 + faceWarp * 1.6);
        x += jx;
        z += jz;
        x += nearest.normalX * side * lateralWarp + nearest.normalZ * alongWarp;
        z += nearest.normalZ * side * lateralWarp - nearest.normalX * alongWarp;
        positions.setX(i, x);
        positions.setZ(i, z);
      }
    }
    positions.setY(i, sample(x, z));
  }
  positions.needsUpdate = true;
  geom.computeVertexNormals();

  // Pass 2 — paint vertex colours from height + slope + sun-direction so the
  // terrain reads as a sunset canyon: cool-violet shadow on faces turned away
  // from the sun, warm rust in the valleys, golden rim on lit ridges.
  const normals = geom.getAttribute('normal') as THREE.BufferAttribute;
  const colors = new Float32Array(positions.count * 3);
  const tmp = new THREE.Color();
  for (let i = 0; i < positions.count; i++) {
    const x = positions.getX(i);
    const y = positions.getY(i);
    const z = positions.getZ(i);
    const altitude = y - TERRAIN_BASE_Y;
    const ny = normals.getY(i);
    const slope = Math.max(0, 1 - ny);
    const lightDot = Math.max(0,
      normals.getX(i) * SUN_DIR.x + ny * SUN_DIR.y + normals.getZ(i) * SUN_DIR.z,
    );
    const variation = fbm2(x * 0.04, z * 0.04, 2);
    paintTerrainVertex(tmp, altitude, slope, lightDot, variation, palette);
    colors[i * 3 + 0] = tmp.r;
    colors[i * 3 + 1] = tmp.g;
    colors[i * 3 + 2] = tmp.b;
  }
  geom.setAttribute('color', new THREE.BufferAttribute(colors, 3));

  const shaderMode = options.shaderMode ?? 'detailed';
  const rendererMode = options.rendererMode ?? 'webgl2';
  const material = createTerrainMaterial(shaderMode, options.bakedSurface ?? null, rendererMode, style);
  material.userData.terrainShaderMode = shaderMode;
  let groundUniforms: GroundShaderUniforms | null = null;
  if (shaderMode === 'detailed' && rendererMode !== 'webgpu') {
    if (canyon) applyCanyonSurfaceDetail(material, makeCanyonRockDetailTexture());
    groundUniforms = installGroundShader(material, style.ground, options.sandTextures);
  }
  if (rendererMode !== 'webgpu') {
    installAerialPerspective(material, rendererMode);
  } else {
    // WebGPU: aerial perspective is global via `scene.fogNode`. Mark the
    // material so the compatibility report knows it intentionally skipped
    // the per-material installer.
    material.fog = true;
    material.userData.webgpuTerrainMaterial = true;
  }
  // Value composition has a WebGPU node-material path (multiplies colorNode
  // by a bg-bucket exposure uniform), so call it for both renderers.
  installValueComposition(material, 'bg', rendererMode);
  // Build a chunked terrain. The unified geometry above carries the final
  // displacement, normals, and vertex colors; we now slice it into a grid
  // of indexed sub-geometries so Three.js can frustum-cull chunks that are
  // outside the view. All chunks share `material`, so per-frame uniform
  // updates (aerial perspective, ground shader) still apply once.
  // `geom.dispose()` (below) only frees the GPU buffer; the underlying
  // typed arrays survive, so we can keep cheap references for repaint.
  const sourcePositions = positions.array as Float32Array;
  const sourceNormals = normals.array as Float32Array;
  const sourceColors = colors;
  const sourceUv = (geom.getAttribute('uv') as THREE.BufferAttribute).array as Float32Array;
  const vertsPerRow = segments + 1;

  const group = new THREE.Group();
  group.name = 'desert-terrain';
  group.updateMatrix();
  group.matrixAutoUpdate = false;
  tagValueBucket(group, 'bg');

  interface TerrainChunk {
    geometry: THREE.BufferGeometry;
    colorAttr: THREE.BufferAttribute;
    sourceIndices: Uint32Array;
  }
  const chunks: TerrainChunk[] = [];
  const chunkCount = TERRAIN_CHUNK_COUNT;
  const baseSegPerChunk = Math.ceil(segments / chunkCount);
  for (let cz = 0; cz < chunkCount; cz++) {
    const rowStart = cz * baseSegPerChunk;
    const rowEnd = Math.min(segments, (cz + 1) * baseSegPerChunk);
    if (rowEnd <= rowStart) continue;
    for (let cx = 0; cx < chunkCount; cx++) {
      const colStart = cx * baseSegPerChunk;
      const colEnd = Math.min(segments, (cx + 1) * baseSegPerChunk);
      if (colEnd <= colStart) continue;
      const colCount = colEnd - colStart + 1;
      const rowCount = rowEnd - rowStart + 1;
      const vertCount = colCount * rowCount;
      const positionsOut = new Float32Array(vertCount * 3);
      const normalsOut = new Float32Array(vertCount * 3);
      const colorsOut = new Float32Array(vertCount * 3);
      const uvOut = new Float32Array(vertCount * 2);
      const sourceIndices = new Uint32Array(vertCount);
      for (let r = 0; r < rowCount; r++) {
        for (let c = 0; c < colCount; c++) {
          const srcIdx = (rowStart + r) * vertsPerRow + (colStart + c);
          const dstIdx = r * colCount + c;
          sourceIndices[dstIdx] = srcIdx;
          positionsOut[dstIdx * 3] = sourcePositions[srcIdx * 3];
          positionsOut[dstIdx * 3 + 1] = sourcePositions[srcIdx * 3 + 1];
          positionsOut[dstIdx * 3 + 2] = sourcePositions[srcIdx * 3 + 2];
          normalsOut[dstIdx * 3] = sourceNormals[srcIdx * 3];
          normalsOut[dstIdx * 3 + 1] = sourceNormals[srcIdx * 3 + 1];
          normalsOut[dstIdx * 3 + 2] = sourceNormals[srcIdx * 3 + 2];
          colorsOut[dstIdx * 3] = sourceColors[srcIdx * 3];
          colorsOut[dstIdx * 3 + 1] = sourceColors[srcIdx * 3 + 1];
          colorsOut[dstIdx * 3 + 2] = sourceColors[srcIdx * 3 + 2];
          uvOut[dstIdx * 2] = sourceUv[srcIdx * 2];
          uvOut[dstIdx * 2 + 1] = sourceUv[srcIdx * 2 + 1];
        }
      }
      // PlaneGeometry's quad triangulation (after rotateX(-π/2)) per quad
      // (a, b, c, d) laid out as:
      //   a---d
      //   |  /|     triangles: (a, b, d) and (b, c, d)
      //   | / |
      //   b---c
      // Reproduce the same winding on chunk-local indices.
      const quadCount = (rowCount - 1) * (colCount - 1);
      const indexArr = new (quadCount * 6 > 65535 ? Uint32Array : Uint16Array)(quadCount * 6);
      let ix = 0;
      for (let r = 0; r < rowCount - 1; r++) {
        for (let c = 0; c < colCount - 1; c++) {
          const a = r * colCount + c;
          const b = (r + 1) * colCount + c;
          const d = r * colCount + (c + 1);
          const e = (r + 1) * colCount + (c + 1);
          indexArr[ix++] = a; indexArr[ix++] = b; indexArr[ix++] = d;
          indexArr[ix++] = b; indexArr[ix++] = e; indexArr[ix++] = d;
        }
      }
      const chunkGeom = new THREE.BufferGeometry();
      chunkGeom.setAttribute('position', new THREE.BufferAttribute(positionsOut, 3));
      chunkGeom.setAttribute('normal', new THREE.BufferAttribute(normalsOut, 3));
      const colorAttr = new THREE.BufferAttribute(colorsOut, 3);
      chunkGeom.setAttribute('color', colorAttr);
      chunkGeom.setAttribute('uv', new THREE.BufferAttribute(uvOut, 2));
      chunkGeom.setIndex(new THREE.BufferAttribute(indexArr, 1));
      chunkGeom.computeBoundingBox();
      chunkGeom.computeBoundingSphere();
      const chunkMesh = new THREE.Mesh(chunkGeom, material);
      chunkMesh.name = `desert-terrain-chunk-${cx}-${cz}`;
      chunkMesh.receiveShadow = true;
      chunkMesh.castShadow = false;
      chunkMesh.updateMatrix();
      chunkMesh.matrixAutoUpdate = false;
      group.add(chunkMesh);
      chunks.push({ geometry: chunkGeom, colorAttr, sourceIndices });
    }
  }
  // The unified geometry was only a scratch buffer for the per-vertex
  // passes above; the GPU only sees the per-chunk geometries from here on.
  geom.dispose();

  let lastPaletteSig = paletteSignature(style.ground);
  let lastSaturation = style.ground.saturationBoost;

  const repaint = (nextStyle: StyleConfig): void => {
    const nextPalette = paletteFromStyle(nextStyle.ground);
    const tmp2 = new THREE.Color();
    const totalVerts = vertsPerRow * vertsPerRow;
    for (let i = 0; i < totalVerts; i++) {
      const x = sourcePositions[i * 3];
      const y = sourcePositions[i * 3 + 1];
      const z = sourcePositions[i * 3 + 2];
      const altitude = y - TERRAIN_BASE_Y;
      const nx = sourceNormals[i * 3];
      const ny = sourceNormals[i * 3 + 1];
      const nz = sourceNormals[i * 3 + 2];
      const slope = Math.max(0, 1 - ny);
      const lightDot = Math.max(0, nx * SUN_DIR.x + ny * SUN_DIR.y + nz * SUN_DIR.z);
      const variation = fbm2(x * 0.04, z * 0.04, 2);
      paintTerrainVertex(tmp2, altitude, slope, lightDot, variation, nextPalette);
      sourceColors[i * 3] = tmp2.r;
      sourceColors[i * 3 + 1] = tmp2.g;
      sourceColors[i * 3 + 2] = tmp2.b;
    }
    // Fan out to each chunk's color buffer. The source-index map was
    // captured during chunk build, so the per-chunk Float32Array slots are
    // written in their native order — no per-vertex re-mapping cost here.
    for (const chunk of chunks) {
      const dst = chunk.colorAttr.array as Float32Array;
      const map = chunk.sourceIndices;
      for (let i = 0; i < map.length; i++) {
        const srcIdx = map[i];
        dst[i * 3] = sourceColors[srcIdx * 3];
        dst[i * 3 + 1] = sourceColors[srcIdx * 3 + 1];
        dst[i * 3 + 2] = sourceColors[srcIdx * 3 + 2];
      }
      chunk.colorAttr.needsUpdate = true;
    }
  };

  const applyStyle = (nextStyle: StyleConfig): void => {
    const sig = paletteSignature(nextStyle.ground);
    if (sig !== lastPaletteSig || nextStyle.ground.saturationBoost !== lastSaturation) {
      // Saturation change repaints because the shader chroma push compounds
      // with the baked vertex tint — keeping them in sync avoids drift on
      // ridge-rim highlights.
      repaint(nextStyle);
      lastPaletteSig = sig;
      lastSaturation = nextStyle.ground.saturationBoost;
    }
    if (groundUniforms) applyGroundUniforms(groundUniforms, nextStyle.ground);
    const webgpuUniforms = material.userData.webgpuGroundUniforms as WebGPUTerrainUniforms | undefined;
    if (webgpuUniforms) applyWebGPUTerrainUniforms(webgpuUniforms, nextStyle.ground);
  };

  return { mesh: group, sampleHeight: sample, applyStyle };
}

function createTerrainMaterial(
  mode: TerrainShaderMode,
  bakedSurface: BakedPbrSurface | null,
  rendererMode: RendererMode,
  style: StyleConfig,
): THREE.MeshStandardMaterial {
  if (rendererMode === 'webgpu') {
    if (mode === 'bakedPbr' && bakedSurface) {
      return makeWebGPUBakedTerrainMaterial(bakedSurface) as unknown as THREE.MeshStandardMaterial;
    }
    return makeWebGPUTerrainNodeMaterial(style.ground) as unknown as THREE.MeshStandardMaterial;
  }
  const material = new THREE.MeshStandardMaterial({
    roughness: 1,
    metalness: 0,
    vertexColors: true,
    flatShading: false,
    map: null,
  });
  if (mode === 'bakedPbr' && bakedSurface) {
    material.map = loadBakedTerrainTexture(bakedSurface.albedoUrl);
    if (bakedSurface.normalUrl) {
      material.normalMap = loadBakedTerrainTexture(bakedSurface.normalUrl, false);
      const scale = bakedSurface.normalScale ?? 1;
      material.normalScale = new THREE.Vector2(scale, scale);
    }
    if (bakedSurface.roughnessUrl) material.roughnessMap = loadBakedTerrainTexture(bakedSurface.roughnessUrl, false);
    material.userData.bakedPbrSurface = bakedSurface;
  }
  return material;
}

function makeWebGPUBakedTerrainMaterial(bakedSurface: BakedPbrSurface): MeshStandardNodeMaterial {
  // Vertex colors are baked in Pass 2 of buildTerrain (height + slope + sun).
  // Without `vertexColors: true` the attribute is never uploaded, so the
  // baked-PBR map renders untinted by the per-vertex sunset palette.
  const material = new MeshStandardNodeMaterial({
    roughness: 1,
    metalness: 0,
    vertexColors: true,
    flatShading: false,
    map: loadBakedTerrainTexture(bakedSurface.albedoUrl),
    toneMapped: true,
  });
  if (bakedSurface.normalUrl) {
    material.normalMap = loadBakedTerrainTexture(bakedSurface.normalUrl, false);
    const scale = bakedSurface.normalScale ?? 1;
    material.normalScale = new THREE.Vector2(scale, scale);
  }
  if (bakedSurface.roughnessUrl) material.roughnessMap = loadBakedTerrainTexture(bakedSurface.roughnessUrl, false);
  material.userData.bakedPbrSurface = bakedSurface;
  return material;
}

function loadBakedTerrainTexture(url: string, srgb = true): THREE.Texture {
  const texture = new THREE.TextureLoader().load(url);
  texture.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  // Bump from 6 → 16: terrain stretches kilometres ahead of the camera,
  // so the shallow-angle sample cost is what dictates how mushy the dunes
  // read at the horizon. Hardware max on the platforms we run on; almost
  // free under WebGPU's MSAA pipeline.
  texture.anisotropy = 16;
  return texture;
}

interface WebGPUTerrainUniforms {
  uGroundSaturation: { value: number };
  uGroundSpeckleIntensity: { value: number };
  uGroundSpeckleDensity: { value: number };
  uGroundPatchIntensity: { value: number };
  uGroundPatchScale: { value: number };
  uGroundGlintColor: { value: THREE.Vector3 };
  uGroundGlintIntensity: { value: number };
  uGroundGlintExponent: { value: number };
  uGroundSunDir: { value: THREE.Vector3 };
}

const groundHashNode = makeWgslFn(`
fn ground_hash(p: vec2<f32>) -> f32 {
  var q = fract(p * vec2<f32>(123.34, 456.21));
  q = q + dot(q, q + vec2<f32>(45.32, 45.32));
  return fract(q.x * q.y);
}
`);

const groundNoiseNode = makeWgslFn(`
fn ground_noise(p: vec2<f32>) -> f32 {
  let i = floor(p);
  let f0 = fract(p);
  let f = f0 * f0 * (vec2<f32>(3.0, 3.0) - 2.0 * f0);
  let a = ground_hash(i);
  let b = ground_hash(i + vec2<f32>(1.0, 0.0));
  let c = ground_hash(i + vec2<f32>(0.0, 1.0));
  let d = ground_hash(i + vec2<f32>(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}
`, [groundHashNode]);

const groundFbmNode = makeWgslFn(`
fn ground_fbm(p: vec2<f32>) -> f32 {
  var q = p;
  var v = 0.0;
  var a = 0.5;
  v = v + ground_noise(q) * a;
  q = q * 2.03;
  a = a * 0.5;
  v = v + ground_noise(q) * a;
  q = q * 2.07;
  a = a * 0.5;
  v = v + ground_noise(q) * a;
  return v / 0.875;
}
`, [groundNoiseNode]);

const groundColorNode = makeWgslFn(`
fn ground_color(
  baseColor: vec3<f32>,
  worldPosition: vec3<f32>,
  worldNormal: vec3<f32>,
  saturation: f32,
  speckleIntensity: f32,
  speckleDensity: f32,
  patchIntensity: f32,
  patchScale: f32
) -> vec3<f32> {
  let lum = dot(baseColor, vec3<f32>(0.299, 0.587, 0.114));
  var color = mix(vec3<f32>(lum, lum, lum), baseColor, saturation);

  // 'patch' is a reserved keyword in WGSL (tessellation), so the shader
  // failed to compile and the terrain rendered as the bright fallback.
  let patchValue = ground_fbm(worldPosition.xz * patchScale);
  color = color * mix(1.0 - patchIntensity, 1.0 + patchIntensity * 0.5, patchValue);

  let speck = step(speckleDensity, ground_hash(floor(worldPosition.xz * 200.0)));
  color = mix(color, color * 0.78, speck * speckleIntensity);

  let duneDerivative = length(vec2<f32>(dpdx(worldPosition.y), dpdy(worldPosition.y)));
  let creviceAo = smoothstep(0.018, 0.16, duneDerivative)
    * smoothstep(0.22, 0.86, 1.0 - abs(normalize(worldNormal).y));
  return color * mix(1.0, 0.82, creviceAo);
}
`, [groundFbmNode]);

const groundGlintNode = makeWgslFn(`
fn ground_glint(
  worldPosition: vec3<f32>,
  worldNormal: vec3<f32>,
  cameraWorldPosition: vec3<f32>,
  sunDir: vec3<f32>,
  glintColor: vec3<f32>,
  glintIntensity: f32,
  glintExponent: f32
) -> vec3<f32> {
  let normal = normalize(worldNormal);
  let viewDir = normalize(cameraWorldPosition - worldPosition);
  let sun = normalize(sunDir);
  let ndl = max(0.0, dot(normal, sun));
  let crest = smoothstep(0.55, 0.95, ndl);
  let view = pow(1.0 - max(0.0, dot(normal, viewDir)), max(0.001, glintExponent));
  let halfDir = normalize(sun + viewDir);
  let sparkle = pow(max(0.0, dot(normal, halfDir)), 600.0)
    * step(0.86, ground_hash(floor(worldPosition.xz * 200.0)));
  return glintColor * glintIntensity * crest * (view + sparkle * 1.8);
}
`, [groundHashNode]);

function makeWebGPUTerrainNodeMaterial(ground: StyleConfig['ground']): MeshStandardNodeMaterial {
  const uniforms = makeWebGPUTerrainUniforms(ground);
  // colorNode below reads `vertexColor()` to drive the height/slope/sun palette
  // baked in Pass 2 of buildTerrain. The attribute only uploads when the
  // material flag is on; otherwise dunes/mountains render flat black.
  const material = new MeshStandardNodeMaterial({
    roughness: 1,
    metalness: 0,
    vertexColors: true,
    flatShading: false,
    toneMapped: true,
  });
  material.colorNode = groundColorNode({
    baseColor: makeNodeVertexColor().rgb,
    worldPosition: positionWorld,
    worldNormal: normalWorld,
    saturation: uniforms.uGroundSaturation,
    speckleIntensity: uniforms.uGroundSpeckleIntensity,
    speckleDensity: uniforms.uGroundSpeckleDensity,
    patchIntensity: uniforms.uGroundPatchIntensity,
    patchScale: uniforms.uGroundPatchScale,
  });
  material.emissiveNode = groundGlintNode({
    worldPosition: positionWorld,
    worldNormal: normalWorld,
    cameraWorldPosition: cameraPosition,
    sunDir: uniforms.uGroundSunDir,
    glintColor: uniforms.uGroundGlintColor,
    glintIntensity: uniforms.uGroundGlintIntensity,
    glintExponent: uniforms.uGroundGlintExponent,
  });
  material.userData.webgpuGroundUniforms = uniforms;
  return material;
}

function makeWebGPUTerrainUniforms(ground: StyleConfig['ground']): WebGPUTerrainUniforms {
  return {
    uGroundSaturation: makeNodeUniform(ground.saturationBoost),
    uGroundSpeckleIntensity: makeNodeUniform(ground.speckleIntensity),
    uGroundSpeckleDensity: makeNodeUniform(ground.speckleDensity),
    uGroundPatchIntensity: makeNodeUniform(ground.patchIntensity),
    uGroundPatchScale: makeNodeUniform(ground.patchScale),
    uGroundGlintColor: makeNodeUniform(groundColorVector(ground.glintColorHex)),
    uGroundGlintIntensity: makeNodeUniform(ground.glintIntensity),
    uGroundGlintExponent: makeNodeUniform(ground.glintExponent),
    uGroundSunDir: makeNodeUniform(SUN_DIR.clone()),
  };
}

function applyWebGPUTerrainUniforms(
  uniforms: WebGPUTerrainUniforms,
  ground: StyleConfig['ground'],
): void {
  uniforms.uGroundSaturation.value = ground.saturationBoost;
  uniforms.uGroundSpeckleIntensity.value = ground.speckleIntensity;
  uniforms.uGroundSpeckleDensity.value = ground.speckleDensity;
  uniforms.uGroundPatchIntensity.value = ground.patchIntensity;
  uniforms.uGroundPatchScale.value = ground.patchScale;
  setGroundColorVector(uniforms.uGroundGlintColor.value, ground.glintColorHex);
  uniforms.uGroundGlintIntensity.value = ground.glintIntensity;
  uniforms.uGroundGlintExponent.value = ground.glintExponent;
}

function groundColorVector(hex: string): THREE.Vector3 {
  const color = new THREE.Color(hex);
  return new THREE.Vector3(color.r, color.g, color.b);
}

function setGroundColorVector(target: THREE.Vector3, hex: string): void {
  const color = new THREE.Color(hex);
  target.set(color.r, color.g, color.b);
}

function paletteSignature(g: StyleConfig['ground']): string {
  return `${g.shadowHex}|${g.valleyHex}|${g.sandHex}|${g.ridgeHex}|${g.peakHex}`;
}

function applyCanyonSurfaceDetail(
  material: THREE.MeshStandardMaterial,
  rockDetailTexture: THREE.DataTexture,
): void {
  material.userData.canyonRockDetailTexture = rockDetailTexture;
  // `#include <...>` directives are not expanded inside shader.fragmentShader
  // at this point. Replacing an include directive is safe; replacing code from
  // inside a chunk, such as directional light loops, silently no-ops unless you
  // splice in THREE.ShaderChunk content yourself first.
  material.onBeforeCompile = (shader) => {
    shader.uniforms.canyonRockDetailMap = { value: rockDetailTexture };
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>
varying vec3 vTerrainWorldPosition;
varying vec3 vTerrainWorldNormal;
varying vec3 vTerrainViewUp;`,
      )
      .replace(
        '#include <beginnormal_vertex>',
        `#include <beginnormal_vertex>
vTerrainWorldNormal = normalize(mat3(modelMatrix) * objectNormal);
vTerrainViewUp = normalize(normalMatrix * vec3(0.0, 1.0, 0.0));`,
      )
      .replace(
        '#include <worldpos_vertex>',
        `#include <worldpos_vertex>
vTerrainWorldPosition = worldPosition.xyz;`,
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
varying vec3 vTerrainWorldPosition;
varying vec3 vTerrainWorldNormal;
varying vec3 vTerrainViewUp;
uniform sampler2D canyonRockDetailMap;

float terrainHash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float terrainNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = terrainHash(i);
  float b = terrainHash(i + vec2(1.0, 0.0));
  float c = terrainHash(i + vec2(0.0, 1.0));
  float d = terrainHash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float terrainFbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  v += terrainNoise(p) * a; p *= 2.03; a *= 0.5;
  v += terrainNoise(p) * a; p *= 2.07; a *= 0.5;
  v += terrainNoise(p) * a;
  return v / 0.875;
}

float terrainRidge(vec2 p) {
  float n = terrainFbm(p);
  return 1.0 - abs(n * 2.0 - 1.0);
}`,
      )
      .replace(
        '#include <normal_fragment_begin>',
        `#include <normal_fragment_begin>
float canyonNormalHeight = smoothstep(8.0, 30.0, max(0.0, vTerrainWorldPosition.y + 0.15));
float canyonNormalSlope = smoothstep(0.36, 0.82, 1.0 - abs(normalize(vTerrainWorldNormal).y));
normal = normalize(mix(normal, normalize(vTerrainViewUp), canyonNormalHeight * canyonNormalSlope * 0.18));
vec3 canyonViewAcross = normalize(cross(normalize(vTerrainViewUp), normal) + vec3(0.0001, 0.0, 0.0));
vec3 canyonViewDown = normalize(cross(normal, canyonViewAcross) + vec3(0.0, 0.0001, 0.0));
float canyonFaceU = dot(vTerrainWorldPosition, normalize(cross(normalize(vec3(0.0, -1.0, 0.0) - normalize(vTerrainWorldNormal) * dot(vec3(0.0, -1.0, 0.0), normalize(vTerrainWorldNormal)) + vec3(0.0001, 0.0, 0.0001)), normalize(vTerrainWorldNormal)) + vec3(0.0001, 0.0, 0.0001)));
float canyonFaceV = max(0.0, vTerrainWorldPosition.y + 0.15);
float canyonFaceW = dot(vTerrainWorldPosition, normalize(vTerrainWorldNormal));
float canyonNormalWarp = terrainFbm(vec2(canyonFaceU * 0.012 + canyonFaceW * 0.010, canyonFaceV * 0.026));
float canyonNormalRibs = terrainRidge(vec2(canyonFaceU * 0.034 + canyonNormalWarp * 2.6, canyonFaceV * 0.041 + canyonFaceW * 0.012));
float canyonNormalPits = terrainFbm(vec2(canyonFaceU * 0.070 + canyonFaceW * 0.020, canyonFaceV * 0.055 + 9.3));
vec2 canyonRockUv = vec2(
  canyonFaceU * 0.007 + canyonFaceW * 0.004 + canyonNormalWarp * 0.18,
  canyonFaceV * 0.011 + canyonFaceW * 0.002
);
vec4 canyonRockDetail = texture2D(canyonRockDetailMap, canyonRockUv);
float canyonDetailMask = canyonNormalHeight
  * canyonNormalSlope
  * smoothstep(0.30, 0.82, canyonNormalRibs + canyonRockDetail.g * 0.42);
normal = normalize(normal
  + canyonViewAcross * ((canyonNormalRibs - 0.48) * 0.22 + (canyonRockDetail.r - 0.50) * 0.26) * canyonDetailMask
  + canyonViewDown * ((canyonNormalPits - 0.55) * 0.14 + (canyonRockDetail.b - 0.52) * 0.24) * canyonDetailMask);`,
      )
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
float terrainHeight = max(0.0, vTerrainWorldPosition.y + 0.15);
float terrainSlope = clamp(1.0 - abs(normalize(vTerrainWorldNormal).y), 0.0, 1.0);
float macroPatch = terrainNoise(vTerrainWorldPosition.xz * 0.026);
float verticalRun = terrainNoise(vec2(vTerrainWorldPosition.x * 0.018 + terrainHeight * 0.045, vTerrainWorldPosition.z * 0.018));
float finePatch = terrainNoise(vTerrainWorldPosition.xz * 0.105 + macroPatch * 3.7);
float bandPhase = macroPatch * 2.6 + finePatch * 0.9;
float band = 1.0 - smoothstep(0.018, 0.105, abs(fract((terrainHeight + bandPhase) / 11.2) - 0.5));
float stain = smoothstep(0.55, 0.92, verticalRun) * terrainSlope;
float cliffMask = smoothstep(0.42, 0.82, terrainSlope) * smoothstep(7.0, 24.0, terrainHeight);
vec3 faceNormal = normalize(vTerrainWorldNormal);
vec3 faceDown = normalize(vec3(0.0, -1.0, 0.0) - faceNormal * dot(vec3(0.0, -1.0, 0.0), faceNormal) + vec3(0.0001, 0.0, 0.0001));
vec3 faceAcross = normalize(cross(faceDown, faceNormal) + vec3(0.0001, 0.0, 0.0001));
float faceU = dot(vTerrainWorldPosition, faceAcross);
float faceV = dot(vTerrainWorldPosition, faceDown);
float faceW = dot(vTerrainWorldPosition, faceNormal);
vec2 wallWarp = vec2(
  terrainFbm(vec2(faceU * 0.010 + faceW * 0.006, faceV * 0.007 + 5.7)),
  terrainFbm(vec2(faceU * 0.014 - faceW * 0.004, faceV * 0.009 + 9.1))
) - 0.5;
vec2 wallUv = vec2(faceU * 0.022 + faceW * 0.006, faceV * 0.030) + wallWarp * 5.4;
float streakSeed = terrainRidge(vec2(faceU * 0.022 + wallWarp.x * 2.5, faceW * 0.018 + macroPatch * 1.7));
float streakBreak = smoothstep(0.40, 0.80, terrainFbm(vec2(faceU * 0.011 + faceV * 0.034, faceV * 0.019 + faceW * 0.026)));
float verticalStreak = pow(streakSeed, 2.3) * streakBreak;
float lateralBreak = smoothstep(0.38, 0.82, terrainFbm(vec2(faceU * 0.046 + faceW * 0.024, faceV * 0.018 + wallWarp.y * 0.9)));
float crackField = terrainRidge(wallUv * vec2(1.85, 0.64) + vec2(faceW * 0.010, macroPatch * 1.8));
float crackGate = smoothstep(0.54, 0.86, terrainFbm(wallUv * 0.92 + vec2(faceW * 0.024, 3.4)));
float brokenCrack = pow(crackField, 4.8) * crackGate * cliffMask;
float pocket = smoothstep(0.66, 0.95, terrainFbm(wallUv * 2.8 + vec2(faceW * 0.032, 7.3))) * cliffMask;
float hardLedge = band * cliffMask * smoothstep(0.48, 0.88, terrainFbm(wallUv * 1.4 + vec2(5.1, faceW * 0.016)));
float blockBreak = pow(terrainRidge(vec2(faceU * 0.038 + faceW * 0.014, faceV * 0.027 + wallWarp.x * 0.8)), 2.4) * lateralBreak * cliffMask;
vec2 proceduralRockUv = vec2(
  faceU * 0.006 + faceW * 0.005 + terrainFbm(vec2(faceU * 0.003, faceV * 0.009)) * 0.22,
  faceV * 0.010 + faceW * 0.003 + terrainFbm(vec2(faceU * 0.008 + 4.7, faceV * 0.004)) * 0.18
);
vec4 canyonRockColorDetail = texture2D(canyonRockDetailMap, proceduralRockUv);
float macroFaceBreak = terrainFbm(vec2(faceU * 0.008 + faceV * 0.012, faceW * 0.018 + macroPatch * 2.3));
float wormShade = terrainRidge(vec2(faceU * 0.018 + faceV * 0.006, faceV * 0.030 + faceW * 0.010));
float bakedStrata = canyonRockColorDetail.r * cliffMask;
float bakedOcclusion = max(canyonRockColorDetail.g, wormShade * macroFaceBreak) * cliffMask;
float bakedPits = max(canyonRockColorDetail.b, terrainFbm(vec2(faceU * 0.022 + faceW * 0.019, faceV * 0.026))) * cliffMask;
float bakedLedges = canyonRockColorDetail.a * cliffMask;
float patina = mix(0.90, 1.12, macroPatch) * mix(0.96, 1.05, finePatch);
diffuseColor.rgb *= patina;
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.58, 0.45, 0.39), stain * 0.18);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.74, 0.56, 0.44), band * terrainSlope * 0.08);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.48, 0.36, 0.32), brokenCrack * 0.24);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.68, 0.50, 0.43), pocket * 0.18);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.52, 0.39, 0.34), verticalStreak * lateralBreak * cliffMask * 0.04);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.62, 0.47, 0.39), blockBreak * 0.16);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(1.10, 0.96, 0.82), hardLedge * 0.08);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.46, 0.35, 0.30), bakedOcclusion * 0.26);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.66, 0.48, 0.40), bakedPits * 0.18);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(1.10, 0.94, 0.78), bakedLedges * 0.12);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.74, 0.54, 0.43), bakedStrata * 0.15);`,
      );
  };
}

interface GroundShaderUniforms {
  uGroundShadow: { value: THREE.Color };
  uGroundSaturation: { value: number };
  uGroundRippleIntensity: { value: number };
  uGroundRippleScale: { value: THREE.Vector2 };
  uGroundRippleAngle: { value: number };
  uGroundSpeckleIntensity: { value: number };
  uGroundSpeckleDensity: { value: number };
  uGroundPatchIntensity: { value: number };
  uGroundPatchScale: { value: number };
  uGroundGlintColor: { value: THREE.Color };
  uGroundGlintIntensity: { value: number };
  uGroundGlintExponent: { value: number };
  uGroundSunDir: { value: THREE.Vector3 };
  uGroundNormalMap?: { value: THREE.Texture };
}

/**
 * Layer the procedural sand surface (wind ripples, speckle, dune-crest glint)
 * onto whatever onBeforeCompile is already in place. Captures the previous
 * compile callback so the canyon-rock pass keeps working — the ground hooks
 * use shader chunks the canyon shader doesn't touch (`<map_fragment>`,
 * `<normal_fragment_maps>`, `<lights_fragment_begin>`).
 */
function installGroundShader(
  material: THREE.MeshStandardMaterial,
  ground: StyleConfig['ground'],
  sandTextures?: SurfaceTextureSet,
): GroundShaderUniforms {
  const reuseCanyonVaryings = !!material.userData.canyonRockDetailTexture;
  const sandNormalMap = sandTextures?.normal ?? null;
  const uniforms: GroundShaderUniforms = {
    uGroundShadow: { value: new THREE.Color(ground.shadowHex) },
    uGroundSaturation: { value: ground.saturationBoost },
    uGroundRippleIntensity: { value: ground.rippleIntensity },
    uGroundRippleScale: { value: new THREE.Vector2(ground.rippleScaleU, ground.rippleScaleV) },
    uGroundRippleAngle: { value: (ground.rippleAngleDeg * Math.PI) / 180 },
    uGroundSpeckleIntensity: { value: ground.speckleIntensity },
    uGroundSpeckleDensity: { value: ground.speckleDensity },
    uGroundPatchIntensity: { value: ground.patchIntensity },
    uGroundPatchScale: { value: ground.patchScale },
    uGroundGlintColor: { value: new THREE.Color(ground.glintColorHex) },
    uGroundGlintIntensity: { value: ground.glintIntensity },
    uGroundGlintExponent: { value: ground.glintExponent },
    uGroundSunDir: { value: SUN_DIR.clone() },
  };
  if (sandNormalMap) uniforms.uGroundNormalMap = { value: sandNormalMap };
  material.userData.groundUniforms = uniforms;
  material.userData.groundSandNormalMap = sandNormalMap;

  const prev = material.onBeforeCompile;
  material.onBeforeCompile = (shader, renderer) => {
    prev?.(shader, renderer);

    Object.assign(shader.uniforms, uniforms);

    // Vertex: declare varyings for world position + normal. Canyon shader has
    // already declared vTerrainWorldPosition / vTerrainWorldNormal — reuse
    // those. Otherwise inject our own.
    if (!reuseCanyonVaryings) {
      shader.vertexShader = shader.vertexShader
        .replace(
          '#include <common>',
          `#include <common>
varying vec3 vGroundWorldPosition;
varying vec3 vGroundWorldNormal;`,
        )
        .replace(
          '#include <beginnormal_vertex>',
          `#include <beginnormal_vertex>
vGroundWorldNormal = normalize(mat3(modelMatrix) * objectNormal);`,
        )
        .replace(
          '#include <worldpos_vertex>',
          `#include <worldpos_vertex>
vGroundWorldPosition = worldPosition.xyz;`,
        );
    }

    const wpVar = reuseCanyonVaryings ? 'vTerrainWorldPosition' : 'vGroundWorldPosition';
    const wnVar = reuseCanyonVaryings ? 'vTerrainWorldNormal' : 'vGroundWorldNormal';

    // Fragment: noise helpers, varyings (only when not reusing canyon's), and
    // the per-pixel surface work (saturation/patches/speckle, ripple normals,
    // dune-crest glint). Each effect is gated by its uniform so dialing the
    // sliders to zero matches the v17 look exactly.
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
${reuseCanyonVaryings ? '' : 'varying vec3 vGroundWorldPosition;\nvarying vec3 vGroundWorldNormal;'}
uniform vec3 uGroundShadow;
uniform float uGroundSaturation;
uniform float uGroundRippleIntensity;
uniform vec2 uGroundRippleScale;
uniform float uGroundRippleAngle;
uniform float uGroundSpeckleIntensity;
uniform float uGroundSpeckleDensity;
uniform float uGroundPatchIntensity;
uniform float uGroundPatchScale;
uniform vec3 uGroundGlintColor;
uniform float uGroundGlintIntensity;
uniform float uGroundGlintExponent;
uniform vec3 uGroundSunDir;
${sandNormalMap ? 'uniform sampler2D uGroundNormalMap;' : ''}

float groundHash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
float groundNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = groundHash(i);
  float b = groundHash(i + vec2(1.0, 0.0));
  float c = groundHash(i + vec2(0.0, 1.0));
  float d = groundHash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}
float groundFbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  v += groundNoise(p) * a; p *= 2.03; a *= 0.5;
  v += groundNoise(p) * a; p *= 2.07; a *= 0.5;
  v += groundNoise(p) * a;
  return v / 0.875;
}
${sandNormalMap ? `
vec2 groundSandNormalSample(vec3 wp, vec3 wn, float meters, vec2 offset) {
  vec3 blend = pow(abs(normalize(wn)), vec3(4.0));
  blend /= max(0.0001, blend.x + blend.y + blend.z);
  vec2 xProj = texture2D(uGroundNormalMap, wp.zy / meters + offset).xy * 2.0 - 1.0;
  vec2 yProj = texture2D(uGroundNormalMap, wp.xz / meters + offset).xy * 2.0 - 1.0;
  vec2 zProj = texture2D(uGroundNormalMap, wp.xy / meters + offset).xy * 2.0 - 1.0;
  return xProj * blend.x + yProj * blend.y + zProj * blend.z;
}` : ''}`,
      )
      .replace(
        // Insert the surface tint work just before lighting so canyon's color
        // mods (which run inside <color_fragment>) and the vertex palette are
        // both already on diffuseColor.rgb.
        '#include <lights_fragment_begin>',
        `// --- ground: saturation / patches / speckle ----------------------
{
  float lum = dot(diffuseColor.rgb, vec3(0.299, 0.587, 0.114));
  diffuseColor.rgb = mix(vec3(lum), diffuseColor.rgb, uGroundSaturation);

  float groundPatch = groundFbm(${wpVar}.xz * uGroundPatchScale);
  diffuseColor.rgb *= mix(1.0 - uGroundPatchIntensity, 1.0 + uGroundPatchIntensity * 0.5, groundPatch);

  float speck = step(uGroundSpeckleDensity, groundHash(floor(${wpVar}.xz * 200.0)));
  diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * 0.78, speck * uGroundSpeckleIntensity);

  float duneDerivative = length(vec2(dFdx(${wpVar}.y), dFdy(${wpVar}.y)));
  float creviceAo = smoothstep(0.018, 0.16, duneDerivative)
    * smoothstep(0.22, 0.86, 1.0 - abs(normalize(${wnVar}).y));
  diffuseColor.rgb *= mix(1.0, 0.82, creviceAo);
}

#include <lights_fragment_begin>

// --- ground: dune-crest glint additive --------------------------
{
  vec3 worldNormal = normalize(${wnVar});
  vec3 viewDirWS = normalize(cameraPosition - ${wpVar});
  float ndl = max(0.0, dot(worldNormal, normalize(uGroundSunDir)));
  float crest = smoothstep(0.55, 0.95, ndl);
  float view = pow(1.0 - max(0.0, dot(worldNormal, viewDirWS)), uGroundGlintExponent);
  vec3 halfDir = normalize(normalize(uGroundSunDir) + viewDirWS);
  float sparkle = pow(max(0.0, dot(worldNormal, halfDir)), 600.0)
    * step(0.86, groundHash(floor(${wpVar}.xz * 200.0)));
  vec3 glint = uGroundGlintColor * uGroundGlintIntensity * crest * (view + sparkle * 1.8);
  reflectedLight.directSpecular += glint;
}`,
      )
      .replace(
        // Anisotropic wind ripples perturb the normal in tangent space using
        // dFdx/dFdy of world position — same trick the asphalt shader uses for
        // its grit micro-relief.
        '#include <normal_fragment_maps>',
        `#include <normal_fragment_maps>
{
  float c = cos(uGroundRippleAngle);
  float s = sin(uGroundRippleAngle);
  vec2 windUv = mat2(c, -s, s, c) * ${wpVar}.xz;
  vec2 sampleUv = windUv * uGroundRippleScale;
  float step = 0.005;
  float rL = groundFbm(sampleUv - vec2(step, 0.0));
  float rR = groundFbm(sampleUv + vec2(step, 0.0));
  float rD = groundFbm(sampleUv - vec2(0.0, step));
  float rU = groundFbm(sampleUv + vec2(0.0, step));
  vec2 grad = vec2(rR - rL, rU - rD);
  ${sandNormalMap ? `
  float viewDist = distance(cameraPosition, ${wpVar});
  vec3 groundNormalWS = normalize(${wnVar});
  vec2 normal2m = groundSandNormalSample(${wpVar}, groundNormalWS, 2.0, vec2(0.17, 0.31));
  vec2 normal01m = groundSandNormalSample(${wpVar}, groundNormalWS, 0.1, vec2(0.43, 0.11));
  float mesoWeight = 1.0 - smoothstep(55.0, 125.0, viewDist);
  float microWeight = 1.0 - smoothstep(10.0, 34.0, viewDist);
  vec2 detailNormal = normal2m * mesoWeight * 0.52
    + normal01m * microWeight * 0.075;
  grad += detailNormal;
  ` : ''}

  vec3 q1 = dFdx(${wpVar});
  vec3 q2 = dFdy(${wpVar});
  vec3 t = normalize(q1);
  vec3 b = normalize(cross(${wnVar}, t));
  vec3 wn = normalize(${wnVar} - (t * grad.x + b * grad.y) * uGroundRippleIntensity);
  normal = normalize((viewMatrix * vec4(wn, 0.0)).xyz);
}`,
      );
  };

  // Force a recompile if the material is already on the GPU.
  material.needsUpdate = true;
  return uniforms;
}

function applyGroundUniforms(
  uniforms: GroundShaderUniforms,
  ground: StyleConfig['ground'],
): void {
  uniforms.uGroundShadow.value.set(ground.shadowHex);
  uniforms.uGroundSaturation.value = ground.saturationBoost;
  uniforms.uGroundRippleIntensity.value = ground.rippleIntensity;
  uniforms.uGroundRippleScale.value.set(ground.rippleScaleU, ground.rippleScaleV);
  uniforms.uGroundRippleAngle.value = (ground.rippleAngleDeg * Math.PI) / 180;
  uniforms.uGroundSpeckleIntensity.value = ground.speckleIntensity;
  uniforms.uGroundSpeckleDensity.value = ground.speckleDensity;
  uniforms.uGroundPatchIntensity.value = ground.patchIntensity;
  uniforms.uGroundPatchScale.value = ground.patchScale;
  uniforms.uGroundGlintColor.value.set(ground.glintColorHex);
  uniforms.uGroundGlintIntensity.value = ground.glintIntensity;
  uniforms.uGroundGlintExponent.value = ground.glintExponent;
}

function makeCanyonRockDetailTexture(): THREE.DataTexture {
  const size = 256;
  const data = new Uint8Array(size * size * 4);

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = (y * size + x) * 4;
      const u = x / size;
      const v = y / size;
      const warpedU = u + (fbm2(u * 5.7 + 12.1, v * 3.9, 3) - 0.5) * 0.18;
      const warpedV = v + (fbm2(u * 3.3, v * 7.1 + 4.2, 3) - 0.5) * 0.12;
      const strataPhase = Math.abs(((warpedV * 34 + fbm2(warpedU * 6.2, warpedV * 1.7, 2) * 2.8) % 1) - 0.5);
      const strata = 1 - smoothstep(0.04, 0.22, strataPhase);
      const verticalCrack = ridgeFbm(warpedU * 20.0 + warpedV * 2.4, warpedV * 3.8, 3);
      const crossBreak = ridgeFbm(warpedU * 6.8 - warpedV * 5.2, warpedV * 16.0, 2);
      const crackGate = smoothstep(0.52, 0.82, fbm2(warpedU * 8.2 + 5.1, warpedV * 7.4, 3));
      const occlusion = Math.max(strata * 0.55, verticalCrack * crossBreak * crackGate);
      const pits = smoothstep(0.58, 0.93, fbm2(warpedU * 19.0 + 17.3, warpedV * 15.0 + 2.1, 3));
      const ledges = Math.max(strata, ridgeFbm(warpedU * 3.6 + warpedV * 0.9, warpedV * 29.0, 2) * 0.65);

      data[i + 0] = Math.round(THREE.MathUtils.clamp(strata * 0.88 + verticalCrack * 0.25, 0, 1) * 255);
      data[i + 1] = Math.round(THREE.MathUtils.clamp(occlusion, 0, 1) * 255);
      data[i + 2] = Math.round(THREE.MathUtils.clamp(pits, 0, 1) * 255);
      data[i + 3] = Math.round(THREE.MathUtils.clamp(ledges, 0, 1) * 255);
    }
  }

  const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  texture.colorSpace = THREE.NoColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;
  return texture;
}

function paintTerrainVertex(
  out: THREE.Color,
  altitude: number,
  slope: number,
  lightDot: number,
  variation: number,
  palette: TerrainPalette,
): void {
  // Altitude-banded base palette.
  if (altitude < 1.5) {
    out.copy(palette.valley).lerp(palette.sand, variation * 0.6);
  } else if (altitude < 14) {
    out.copy(palette.sand).lerp(palette.ridge, (altitude - 1.5) / 12.5);
  } else if (altitude < 32) {
    out.copy(palette.ridge).lerp(palette.peak, (altitude - 14) / 18);
  } else {
    out.copy(palette.peak);
  }

  // Shadow on steep faces turned away from the sun. The slope gate keeps
  // flats warm; lightDot gate spares lit faces. Shadow tint comes from the
  // ground palette so warm-amber replaces the previous cool-violet.
  const shadowMix = slope * (1 - lightDot) * 0.86;
  if (shadowMix > 0) out.lerp(palette.shadow, shadowMix);

  // Subtle rim brighten on lit ridges — gives the painted-poster glow on
  // the sun-facing edges of buttes.
  const rim = lightDot * Math.max(0, slope - 0.05) * 0.22;
  out.r = Math.min(1, out.r + rim * 0.28);
  out.g = Math.min(1, out.g + rim * 0.18);
  out.b = Math.min(1, out.b + rim * 0.16);

  // Patchy painted variation so flat sand isn't a solid colour.
  const wobble = 0.82 + (variation - 0.5) * 0.14;
  out.multiplyScalar(wobble);
}

// ────────────────────────────────────────────────────────────────────────────
// Centerline spatial grid — 3×3 cell lookup for fast distance-to-track.
// ────────────────────────────────────────────────────────────────────────────

interface CenterlineGrid {
  cellSize: number;
  cells: Map<string, number[]>;
}

interface NearestCenterlineSample {
  index: number;
  segmentT: number;
  distance: number;
  signedLateral: number;
  normalX: number;
  normalZ: number;
  widthLeft: number;
  widthRight: number;
  arcLength: number;
}

function buildCenterlineGrid(track: Track, cellSize: number): CenterlineGrid {
  const cells = new Map<string, number[]>();
  for (let i = 0; i < track.centerline.length; i++) {
    const p = track.centerline[i];
    const ci = Math.floor(p.x / cellSize);
    const cj = Math.floor(p.z / cellSize);
    const key = `${ci},${cj}`;
    const arr = cells.get(key);
    if (arr) arr.push(i);
    else cells.set(key, [i]);
  }
  return { cellSize, cells };
}

function nearestCenterlineSample(
  grid: CenterlineGrid,
  track: Track,
  x: number,
  z: number,
): NearestCenterlineSample {
  const n = track.centerline.length;
  const candidateIndices = new Set<number>();
  const ci = Math.floor(x / grid.cellSize);
  const cj = Math.floor(z / grid.cellSize);
  for (let di = -1; di <= 1; di++) {
    for (let dj = -1; dj <= 1; dj++) {
      const arr = grid.cells.get(`${ci + di},${cj + dj}`);
      if (!arr) continue;
      for (const idx of arr) {
        candidateIndices.add(idx);
        candidateIndices.add((idx - 1 + n) % n);
      }
    }
  }
  if (candidateIndices.size === 0) {
    for (let idx = 0; idx < n; idx++) candidateIndices.add(idx);
  }

  let nearestVertex = 0;
  let nearestVertexSq = Infinity;
  for (const idx of candidateIndices) {
    const p = track.centerline[idx];
    const dx = x - p.x;
    const dz = z - p.z;
    const sq = dx * dx + dz * dz;
    if (sq < nearestVertexSq) {
      nearestVertex = idx;
      nearestVertexSq = sq;
    }
  }
  if (nearestVertexSq < 1e-8) {
    const p = track.centerline[nearestVertex];
    const t = track.tangents[nearestVertex];
    const nx = -t.tz;
    const nz = t.tx;
    const dx = x - p.x;
    const dz = z - p.z;
    return {
      index: nearestVertex,
      segmentT: 0,
      distance: Math.sqrt(nearestVertexSq),
      signedLateral: dx * nx + dz * nz,
      normalX: nx,
      normalZ: nz,
      widthLeft: p.widthLeft,
      widthRight: p.widthRight,
      arcLength: track.cumulativeLength[nearestVertex],
    };
  }

  let best = {
    index: 0,
    t: 0,
    x: track.centerline[0].x,
    z: track.centerline[0].z,
    tx: track.tangents[0].tx,
    tz: track.tangents[0].tz,
    widthLeft: track.centerline[0].widthLeft,
    widthRight: track.centerline[0].widthRight,
    arcLength: track.cumulativeLength[0],
    sq: Infinity,
  };

  for (const idx of candidateIndices) {
    const next = (idx + 1) % n;
    const a = track.centerline[idx];
    const b = track.centerline[next];
    const abx = b.x - a.x;
    const abz = b.z - a.z;
    const lenSq = Math.max(abx * abx + abz * abz, 1e-6);
    const t = THREE.MathUtils.clamp(((x - a.x) * abx + (z - a.z) * abz) / lenSq, 0, 1);
    const px = a.x + abx * t;
    const pz = a.z + abz * t;
    const dx = x - px;
    const dz = z - pz;
    const sq = dx * dx + dz * dz;
    if (sq >= best.sq) continue;

    const len = Math.sqrt(lenSq);
    const tx = abx / len;
    const tz = abz / len;
    const segLength = idx === n - 1
      ? Math.hypot(track.centerline[0].x - a.x, track.centerline[0].z - a.z)
      : track.cumulativeLength[next] - track.cumulativeLength[idx];
    best = {
      index: idx,
      t,
      x: px,
      z: pz,
      tx,
      tz,
      widthLeft: lerp(a.widthLeft, b.widthLeft, t),
      widthRight: lerp(a.widthRight, b.widthRight, t),
      arcLength: track.cumulativeLength[idx] + Math.max(0, segLength) * t,
      sq,
    };
  }

  const nx = -best.tz;
  const nz = best.tx;
  const dx = x - best.x;
  const dz = z - best.z;
  return {
    index: best.index,
    segmentT: best.t,
    distance: Math.sqrt(best.sq),
    signedLateral: dx * nx + dz * nz,
    normalX: nx,
    normalZ: nz,
    widthLeft: best.widthLeft,
    widthRight: best.widthRight,
    arcLength: best.arcLength,
  };
}

function sampleClassicTerrain(
  track: Track,
  x: number,
  z: number,
  nearest: NearestCenterlineSample,
): number {
  const roadBaseY = trackSurfaceHeightAtArc(track, nearest.arcLength, nearest.signedLateral) + TERRAIN_BASE_Y;
  if (nearest.distance <= FLAT_RADIUS) return roadBaseY;
  const env = smoothstep(FLAT_RADIUS, FLAT_RADIUS + RAMP_LENGTH, nearest.distance);
  const macro = fbm2(x * 0.0032, z * 0.0032, 5);
  const meso = fbm2(x * 0.011, z * 0.011, 4);
  const ridge = ridgeFbm(x * 0.0078, z * 0.0078, 3);
  const noise = (macro - 0.42) * 50 + meso * 9 + ridge * ridge * 24;
  return lerp(roadBaseY, TERRAIN_BASE_Y, env) + Math.max(0, noise) * env;
}

function sampleCanyonTerrain(
  track: Track,
  x: number,
  z: number,
  nearest: NearestCenterlineSample,
): number {
  const signed = nearest.signedLateral;
  const side: 1 | -1 = signed >= 0 ? 1 : -1;
  const halfWidth = side > 0 ? nearest.widthLeft : nearest.widthRight;
  const absLat = Math.abs(signed);
  const arc = nearest.arcLength;
  const turn = signedTurnAtSample(track, nearest.index, nearest.segmentT, 10);
  const curveStrength = Math.min(1, Math.abs(turn) * 2.6);
  const outsideCorner = Math.max(0, -turn * side) * 2.2;
  const profile = canyonSectionProfile(arc, side, curveStrength, outsideCorner);
  const roadBaseY = trackSurfaceHeightAtArc(track, nearest.arcLength, signed) + TERRAIN_BASE_Y;

  const wallEdge = halfWidth + track.wallBuffer + 8;
  if (absLat <= wallEdge + 7) return roadBaseY;

  const outside = absLat - wallEdge;
  const wallStart = wallEdge + profile.wallSetback;
  const cliffOutside = absLat - wallStart;
  const shoulderEnv = smoothstep(7, Math.max(15, profile.wallSetback * 0.76), outside);
  const cliffEnv = smoothstep(14, 132 + profile.openness * 112, cliffOutside);
  const massEnv = smoothstep(24, 96 + profile.openness * 140, Math.max(0, cliffOutside));
  const headroomEnv = smoothstep(42, 112, outside);
  const faceDepth = Math.max(0, cliffOutside);
  const faceMask = cliffEnv * headroomEnv;
  const nearFaceMask = faceMask * (1 - smoothstep(86, 235, faceDepth));

  const warpFreq = 0.0009;
  const wx = (fbm2(x * warpFreq, z * warpFreq + side * 19.1, 2) - 0.5) * profile.warpAmp;
  const wz = (fbm2(x * warpFreq + 31.7, z * warpFreq + side * 7.4, 2) - 0.5) * profile.warpAmp;
  const sx = x + wx;
  const sz = z + wz;
  const wallDepth = Math.max(0, cliffOutside);
  const wallWarpA = (fbm2(sx * 0.0021 + side * 3.3, sz * 0.0021 + arc * 0.00037, 3) - 0.5) * 220;
  const wallWarpB = (fbm2(sx * 0.0062 + 17.1, sz * 0.0062 + side * 8.4, 2) - 0.5) * 42;
  const wallArc = arc + wallWarpA + signed * 0.26;
  const wallSectionWarp = (fbm2(wallArc * 0.0048 + side * 6.1, wallDepth * 0.024 + side * 2.8, 3) - 0.5) * 78;
  const wallSection = wallDepth + wallWarpB + wallSectionWarp + outsideCorner * 18;

  const macro = fbm2(sx * 0.0027, sz * 0.0027, 5);
  const meso = fbm2(sx * 0.0095, sz * 0.0095, 4);
  const baseProfile = fbm2(wallArc * 0.00125 + side * 9.3, wallSection * 0.0065 + arc * 0.00031, 5);
  const ridgeHardness = ridgeFbm((wallArc + wallSection * 0.34) * 0.0028 + side * 4.4, wallSection * 0.011, 4);
  const wormCut = perlinWormCut(wallArc, faceDepth, side, profile);
  const faultCut = smoothstep(0.58, 0.86, ridgeFbm((wallArc - faceDepth * 1.18) * 0.0038 + side * 2.7, faceDepth * 0.015, 3));
  const rockProfileScale = 0.74 + baseProfile * 0.34 + ridgeHardness * 0.22 - wormCut * nearFaceMask * 0.26;
  const ridgeMajor = ridgeFbm((wallArc + wallSection * 0.26) * 0.0042 + side * 5.7, wallSection * 0.027, 4);
  const ridgeMinor = ridgeFbm((wallArc + wallSection * 0.58) * 0.0105 + side * 11.2, wallSection * 0.046 + arc * 0.0008, 2);
  const face = rockFaceVariation(wallArc, faceDepth, signed, side, profile);
  const diagonalGully = ridgeFbm((wallArc + faceDepth * (0.62 + side * 0.08)) * 0.0062, faceDepth * 0.022 + side * 5.1, 3);
  const brokenShelf = fbm2(wallArc * 0.0081 + side * 6.4, faceDepth * 0.041 + diagonalGully * 1.7, 3);
  const worm = fbm2(sx * 0.0041 + arc * 0.00042, sz * 0.0041 + side * 5.7, 3);
  const gullyCut = 1 - smoothstep(0.37, 0.61, worm) * (0.28 + profile.throat * 0.24)
    - face.alcoveCut * nearFaceMask * (0.18 + profile.throat * 0.16)
    - smoothstep(0.58, 0.86, diagonalGully) * nearFaceMask * (0.10 + profile.throat * 0.11)
    - wormCut * nearFaceMask * (0.20 + profile.throat * 0.18)
    - faultCut * nearFaceMask * 0.09;
  const faceBreak = 0.88
    + fbm2(wallArc * 0.0048 + side * 2.9, wallSection * 0.018 + arc * 0.0007, 3) * 0.13
    + face.buttress * nearFaceMask * 0.22
    + ridgeHardness * nearFaceMask * 0.12
    - face.alcoveCut * nearFaceMask * 0.18
    - smoothstep(0.24, 0.46, brokenShelf) * nearFaceMask * 0.08
    - wormCut * nearFaceMask * 0.14;
  const benchNoise = ridgeFbm(wallArc * 0.0074 + side * 11.7, wallSection * 0.021 + signed * 0.002, 3);

  const basinFloor = Math.max(0, (macro - 0.46) * (8 + profile.openness * 18) + meso * 4)
    * shoulderEnv
    * (0.55 + profile.openness * 0.65);
  const cutShoulder = shoulderEnv
    * (1 - cliffEnv)
    * profile.shoulderLift
    * (0.42 + ridgeMajor * 0.28 + benchNoise * 0.20 + baseProfile * 0.28);
  const brokenBenches = shoulderEnv
    * cliffEnv
    * headroomEnv
    * (benchNoise * (0.50 + face.ledgeBreak * 0.30 + baseProfile * 0.30))
    * (1.4 + profile.throat * 3.6)
    * (1 - profile.openness * 0.35)
    * (1 - wormCut * nearFaceMask * 0.35);
  const nearCliff = cliffEnv
    * headroomEnv
    * profile.wallHeight
    * (0.24 + profile.throat * 0.36 + outsideCorner * 0.12)
    * (0.78 + ridgeMajor * 0.10 + face.buttress * nearFaceMask * 0.34)
    * rockProfileScale
    * faceBreak
    * THREE.MathUtils.clamp(gullyCut, 0.52, 1.12);
  const farMass = Math.max(0, (macro - 0.27) * 76 + meso * 18)
    * massEnv
    * headroomEnv
    * (0.30 + profile.throat * 0.62)
    * (0.86 + baseProfile * 0.26 + ridgeHardness * 0.14)
    * THREE.MathUtils.clamp(gullyCut - face.alcoveCut * faceMask * 0.10 - wormCut * faceMask * 0.08, 0.58, 1.1);
  const ribs = cliffEnv
    * headroomEnv
    * (ridgeMajor * ridgeMajor * (0.62 + profile.wallHeight * 0.012) + ridgeMinor * 0.50 + face.faceRibs * nearFaceMask * 1.35)
    * (0.16 + profile.throat * 0.14)
    * (0.72 + faceBreak * 0.28)
    * (1 - smoothstep(0.50, 0.88, diagonalGully) * nearFaceMask * 0.28)
    * (1 - wormCut * nearFaceMask * 0.62)
    * (1 - faultCut * nearFaceMask * 0.22);

  const rawHeight = basinFloor + cutShoulder + brokenBenches + nearCliff + farMass + ribs;
  const bandHeight = 5.8 + fbm2(arc * 0.004 + side * 8.3, side * 3.1, 2) * 4.2;
  const bandPhase = (fbm2(sx * 0.0058, sz * 0.0058, 2) - 0.5) * bandHeight * 1.4;
  const terraced = softTerrace(rawHeight + bandPhase, bandHeight, 0.24) - bandPhase;
  const strataMix = cliffEnv
    * (0.08 + profile.throat * 0.10)
    * (1 - face.ledgeBreak * nearFaceMask * 0.48);

  return roadBaseY + lerp(rawHeight, terraced, strataMix);
}

interface RockFaceVariation {
  buttress: number;
  alcoveCut: number;
  ledgeBreak: number;
  faceRibs: number;
}

function perlinWormCut(
  wallArc: number,
  faceDepth: number,
  side: 1 | -1,
  profile: CanyonSectionProfile,
): number {
  if (faceDepth <= 0) return 0;

  const macroGate = smoothstep(0.42, 0.74, fbm2(wallArc * 0.0016 + side * 4.7, side * 19.1, 4));
  const centerA = 46
    + fbm2(wallArc * 0.0022 + side * 7.3, side * 5.9, 3) * 124
    + Math.sin(wallArc * 0.0061 + side * 2.4) * 24;
  const widthA = 14 + fbm2(wallArc * 0.0048 + side * 2.1, faceDepth * 0.008, 2) * 34;
  const channelA = (1 - smoothstep(widthA, widthA * 2.9, Math.abs(faceDepth - centerA))) * macroGate;

  const diagonalDepth = ((wallArc * (0.18 + profile.throat * 0.08) + side * 97) % 220 + 220) % 220;
  const diagonalWarp = (fbm2(wallArc * 0.0034 + 11.2, faceDepth * 0.010 + side * 3.8, 3) - 0.5) * 72;
  const channelB = (1 - smoothstep(16, 58, Math.abs(faceDepth - diagonalDepth - diagonalWarp)))
    * smoothstep(0.50, 0.84, ridgeFbm(wallArc * 0.0026 + side * 6.4, faceDepth * 0.014, 3));

  return THREE.MathUtils.clamp(Math.max(channelA, channelB * 0.82), 0, 1);
}

function rockFaceVariation(
  wallArc: number,
  faceDepth: number,
  signed: number,
  side: 1 | -1,
  profile: CanyonSectionProfile,
): RockFaceVariation {
  const depth = Math.max(0, faceDepth);
  const depthFade = 1 - smoothstep(160, 340, depth);
  const u = wallArc * 0.006 + side * 7.9;
  const v = depth * 0.032 + signed * 0.0017;
  const warp = (fbm2(u * 0.42, v * 0.66 + side * 3.3, 3) - 0.5) * (16 + profile.throat * 18);
  const faceU = wallArc + warp + side * 41;
  const faceV = depth + (fbm2(wallArc * 0.0025 + 11.7, depth * 0.015 + side * 5.1, 2) - 0.5) * 62;

  const broadCells = ridgeFbm(faceU * 0.0028, faceV * 0.018 + side * 2.3, 4);
  const pocketNoise = fbm2(faceU * 0.0064 + side * 8.1, faceV * 0.031, 3);
  const ribField = ridgeFbm(faceU * 0.010 + side * 13.7, faceV * 0.052 + pocketNoise * 1.4, 3);
  const ledgeWarp = fbm2(faceU * 0.0045 + side * 2.2, faceV * 0.024 + 6.8, 2);

  const buttress = smoothstep(0.52, 0.86, broadCells)
    * (0.65 + profile.throat * 0.45)
    * depthFade;
  const alcoveCut = smoothstep(0.56, 0.82, pocketNoise)
    * smoothstep(18, 78, depth)
    * depthFade
    * (0.60 + profile.throat * 0.55);
  const ledgeBreak = smoothstep(0.35, 0.78, ledgeWarp)
    * (1 - smoothstep(0.78, 0.98, broadCells))
    * depthFade;
  const faceRibs = Math.pow(THREE.MathUtils.clamp(ribField, 0, 1), 2.2)
    * (1 - alcoveCut * 0.55)
    * depthFade;

  return {
    buttress: THREE.MathUtils.clamp(buttress, 0, 1),
    alcoveCut: THREE.MathUtils.clamp(alcoveCut, 0, 1),
    ledgeBreak: THREE.MathUtils.clamp(ledgeBreak, 0, 1),
    faceRibs: THREE.MathUtils.clamp(faceRibs, 0, 1),
  };
}

interface CanyonSectionProfile {
  openness: number;
  throat: number;
  wallSetback: number;
  wallHeight: number;
  shoulderLift: number;
  warpAmp: number;
}

function canyonSectionProfile(
  arc: number,
  side: 1 | -1,
  curveStrength: number,
  outsideCorner: number,
): CanyonSectionProfile {
  const openness = canyonOpenness(arc, side);
  const broadBasin = smoothstep(0.58, 0.88, fbm2(arc * 0.00115 + side * 4.1, side * 9.7, 3));
  const throatNoise = ridgeFbm(arc * 0.0019 + side * 3.1, side * 14.3, 3);
  const throat = THREE.MathUtils.clamp(
    (1 - openness) * 0.74 + throatNoise * 0.24 + curveStrength * 0.18 + outsideCorner * 0.20 - broadBasin * 0.24,
    0,
    1,
  );
  const sideBreath = (fbm2(arc * 0.0031, side * 21.7, 2) - 0.5) * 32;
  const wallDistance = canyonWallDistance(openness);
  const wallSetback = THREE.MathUtils.clamp(
    wallDistance * (0.78 + broadBasin * 0.42) + sideBreath - throat * 10 - outsideCorner * 16,
    28,
    245,
  );
  const roughness = ridgeFbm((arc + side * 97) * 0.011, side * 12.7, 3);
  const wallHeight = canyonWallHeight(openness, roughness)
    * (0.70 + throat * 0.94 + outsideCorner * 0.22)
    * (1 - broadBasin * 0.36);
  return {
    openness,
    throat,
    wallSetback,
    wallHeight,
    shoulderLift: 1.2 + throat * 6.5 + curveStrength * 2.4,
    warpAmp: 95 + throat * 150 + broadBasin * 55,
  };
}

function signedTurnAtSample(track: Track, index: number, t: number, window: number): number {
  const n = track.tangents.length;
  return lerp(
    signedTurnAt(track, index, window),
    signedTurnAt(track, (index + 1) % n, window),
    t,
  );
}

function signedTurnAt(track: Track, index: number, window: number): number {
  const n = track.tangents.length;
  const a = track.tangents[(index - window + n) % n];
  const b = track.tangents[(index + window) % n];
  const cross = a.tx * b.tz - a.tz * b.tx;
  const dot = a.tx * b.tx + a.tz * b.tz;
  return Math.atan2(cross, dot);
}

function softTerrace(height: number, bandHeight: number, feather: number): number {
  if (height <= 0) return height;
  const band = Math.floor(height / bandHeight);
  const t = (height / bandHeight) - band;
  const step = smoothstep(1 - feather, 1, t);
  return (band + step) * bandHeight;
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

function trackBounds(track: Track): { minX: number; maxX: number; minZ: number; maxZ: number } {
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  for (const p of [...track.wallInnerPolygon, ...track.wallOuterPolygon]) {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minZ = Math.min(minZ, p.z);
    maxZ = Math.max(maxZ, p.z);
  }
  return { minX, maxX, minZ, maxZ };
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = THREE.MathUtils.clamp((x - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
}

function fbm2(x: number, z: number, octaves: number): number {
  let value = 0;
  let amplitude = 0.5;
  let frequency = 1;
  let norm = 0;
  for (let o = 0; o < octaves; o++) {
    value += amplitude * smoothValueNoise(x * frequency, z * frequency);
    norm += amplitude;
    amplitude *= 0.5;
    frequency *= 2;
  }
  return value / Math.max(norm, 1e-6);
}

function ridgeFbm(x: number, z: number, octaves: number): number {
  let value = 0;
  let amplitude = 0.5;
  let frequency = 1;
  let norm = 0;
  for (let o = 0; o < octaves; o++) {
    const n = 1 - Math.abs(smoothValueNoise(x * frequency, z * frequency) * 2 - 1);
    value += amplitude * n * n;
    norm += amplitude;
    amplitude *= 0.5;
    frequency *= 2;
  }
  return value / Math.max(norm, 1e-6);
}

function smoothValueNoise(x: number, z: number): number {
  const xi = Math.floor(x);
  const zi = Math.floor(z);
  const xf = x - xi;
  const zf = z - zi;
  const u = xf * xf * (3 - 2 * xf);
  const v = zf * zf * (3 - 2 * zf);
  const a = hash2i(xi, zi);
  const b = hash2i(xi + 1, zi);
  const c = hash2i(xi, zi + 1);
  const d = hash2i(xi + 1, zi + 1);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}

function hash2i(x: number, z: number): number {
  let h = x * 374761393 + z * 668265263;
  h = (h ^ (h >>> 13)) * 1274126177;
  h = h ^ (h >>> 16);
  return ((h >>> 0) % 100000) / 100000;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
